import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FileSignature, Download, CheckCircle2, X, Clock, ExternalLink, AlertTriangle,
} from "lucide-react";
import api from "../lib/api";
import ConfirmModal from "../components/ConfirmModal";
import UnseenDot from "../components/UnseenDot";
import useUnseen from "../lib/useUnseen";
import useFocusOnMount from "../lib/useFocusOnMount";
import VoiceTextarea from "../components/VoiceTextarea";

const STATUS_CHIPS = {
  pending_review: { label: "Pending review", color: "#F59E0B" },
  approved: { label: "Approved", color: "#10B981" },
  rejected: { label: "Rejected", color: "#F43F5E" },
};

export default function AccountApplicationsList() {
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState("pending_review");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/account-applications");
    setApps(data);
  };
  useEffect(() => {
    load();
  }, []);

  // Awareness Contract — DESIGN_CONTRACT §7.
  const { isUnseen, touch, dismiss } = useUnseen("account_apps", apps);
  useEffect(() => { touch(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useFocusOnMount({ ready: apps.length > 0, deps: [apps.length] });

  const filtered = useMemo(
    () => apps.filter((a) => (tab === "all" ? true : (a.status || "pending_review") === tab)),
    [apps, tab],
  );

  return (
    <div className="space-y-5" data-testid="account-applications-list">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Wave 5 · Account management</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <FileSignature className="w-7 h-7 text-cyan-400" />
            Account applications
          </h1>
          <p className="text-xs text-[#647087] mt-1 max-w-xl">
            Review submitted SA-grade account applications. Approving creates / unlocks the
            customer; rejecting flags it. The public submission link is{" "}
            <span className="font-mono text-cyan-300">/apply/account</span>.
          </p>
        </div>
        <a
          href="/apply/account"
          target="_blank"
          rel="noreferrer"
          className="hv-btn hv-btn-ghost"
          data-testid="open-public-form-btn"
        >
          <ExternalLink className="w-4 h-4" /> Open public form
        </a>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["pending_review", "approved", "rejected", "all"].map((k) => {
          const count = apps.filter((a) => (k === "all" ? true : (a.status || "pending_review") === k)).length;
          const meta = STATUS_CHIPS[k] || { label: "All", color: "#647087" };
          return (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              data-testid={`apps-tab-${k}`}
              className="px-3 py-1.5 rounded-full border text-xs uppercase tracking-wider"
              style={
                tab === k
                  ? { borderColor: meta.color, background: `${meta.color}1a`, color: meta.color }
                  : { borderColor: "rgba(255,255,255,0.1)", color: "#a0aabf" }
              }
            >
              {meta.label} <span className="ml-1 font-mono text-[10px] opacity-80">{count}</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="hv-card p-8 text-center text-sm text-[#647087]">
          No applications in this bucket yet.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((a) => (
          <ApplicationRow
            key={a.id}
            app={a}
            isOpen={openId === a.id}
            onToggle={() => { dismiss(a.id); setOpenId(openId === a.id ? null : a.id); }}
            onChanged={load}
            unseen={isUnseen(a)}
          />
        ))}
      </div>
    </div>
  );
}

function ApplicationRow({ app, isOpen, onToggle, onChanged, unseen }) {
  const status = STATUS_CHIPS[app.status || "pending_review"] || STATUS_CHIPS.pending_review;
  return (
    <div className={`hv-card overflow-hidden ${unseen ? "hv-unseen" : ""}`} data-testid={`app-row-${app.id}`} data-focus-id={app.id}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition text-left"
      >
        <UnseenDot unseen={unseen} testId={`app-unseen-${app.id}`} />
        <Clock className="w-4 h-4 text-[#647087] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{app.trading_name || "Untitled"}</div>
          <div className="text-[11px] text-[#647087] mt-0.5 truncate">
            {app.contact_name || "—"} · {app.contact_email || "no email"}
            {app.requested_credit_limit ? ` · R${app.requested_credit_limit.toLocaleString('en-GB')}` : ""}
            {app.requested_payment_terms ? ` · ${app.requested_payment_terms}` : ""}
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border whitespace-nowrap"
          style={{ color: status.color, borderColor: `${status.color}55`, background: `${status.color}1a` }}
        >
          {status.label}
        </span>
      </button>
      {isOpen && <ApplicationDetail app={app} onChanged={onChanged} />}
    </div>
  );
}

function ApplicationDetail({ app, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [approvedLimit, setApprovedLimit] = useState(app.requested_credit_limit || "");
  const [approvedTerms, setApprovedTerms] = useState(app.requested_payment_terms || "30d");
  const [decisionNotes, setDecisionNotes] = useState("");
  const navigate = useNavigate();

  const editable = (app.status || "pending_review") === "pending_review";

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/account-applications/${app.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `account-application-${(app.trading_name || "untitled").replace(/\s+/g, "_")}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (ex) {
      // eslint-disable-next-line no-alert
      alert(`Could not generate PDF: ${ex?.response?.data?.detail || ex.message || "unknown error"}`);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/account-applications/${app.id}/approve`, {
        approved_credit_limit: approvedLimit ? Number(approvedLimit) : null,
        approved_payment_terms: approvedTerms,
        decision_notes: decisionNotes,
        create_customer_if_missing: true,
      });
      setApproveOpen(false);
      onChanged?.();
      // Jump to the customer record we just unlocked.
      if (data.approved_customer_id) navigate(`/customers/${data.approved_customer_id}`);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    await api.post(`/account-applications/${app.id}/reject`, { decision_notes: rejectReason });
    setConfirmReject(false);
    onChanged?.();
  };

  return (
    <div className="border-t border-white/5 p-4 space-y-4 text-sm">
      <div className="grid md:grid-cols-2 gap-4">
        <KV label="Trading name" value={app.trading_name} />
        <KV label="Trading-as" value={app.trading_as} />
        <KV label="Registration #" value={app.registration_number} />
        <KV label="VAT #" value={app.vat_number} />
        <KV label="Industry" value={app.industry} />
        <KV label="Years in business" value={app.years_in_business} />
        <KV label="Contact" value={`${app.contact_name || "—"}${app.contact_designation ? ` (${app.contact_designation})` : ""}`} />
        <KV label="Phone / email" value={`${app.contact_phone || "—"} · ${app.contact_email || "—"}`} />
        <KV label="Physical" value={[app.physical_address, app.physical_city, app.physical_postal_code].filter(Boolean).join(", ")} />
        <KV label="Postal" value={[app.postal_address, app.postal_city, app.postal_postal_code].filter(Boolean).join(", ")} />
        <KV label="Bank" value={`${app.bank_name || "—"} ${app.bank_branch_code || ""}`} />
        <KV label="Account #" value={app.bank_account_number} />
        <KV label="Requested credit" value={app.requested_credit_limit ? `R ${app.requested_credit_limit.toLocaleString('en-GB')}` : "—"} />
        <KV label="Requested terms" value={app.requested_payment_terms} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Trade references</div>
          {(app.trade_references || []).length === 0 ? (
            <div className="text-xs text-[#647087] italic">None provided.</div>
          ) : (
            <ul className="space-y-1 text-xs">
              {app.trade_references.map((r, i) => (
                <li key={i} className="border border-white/5 rounded px-2 py-1.5">
                  <div className="font-medium">{r.company || "—"}</div>
                  <div className="text-[10px] text-[#647087]">
                    {[r.contact_name, r.phone, r.email].filter(Boolean).join(" · ") || "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Directors / sureties</div>
          {(app.directors || []).length === 0 ? (
            <div className="text-xs text-[#647087] italic">None provided.</div>
          ) : (
            <ul className="space-y-1 text-xs">
              {app.directors.map((d, i) => (
                <li key={i} className="border border-white/5 rounded px-2 py-1.5">
                  <div className="font-medium">{d.full_name || "—"}</div>
                  <div className="text-[10px] text-[#647087]">
                    {d.id_number ? `ID: ${d.id_number}` : ""} {d.cell ? ` · ${d.cell}` : ""}
                    {d.is_surety ? " · 🛡 surety" : ""}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="text-[11px] text-[#647087]">
        Submitted: {app.created_at ? new Date(app.created_at).toLocaleString('en-GB') : "—"}
        {app.signed_by_full_name ? ` by ${app.signed_by_full_name}` : ""}
        {app.terms_accepted ? " · ✓ terms accepted" : " · ⚠ terms NOT accepted"}
        {app.suretyship_acknowledged ? " · ✓ suretyship acknowledged" : ""}
      </div>

      {!app.terms_accepted && (
        <div className="flex items-center gap-2 text-xs text-amber-300 border border-amber-500/40 rounded-md p-2 bg-amber-500/10">
          <AlertTriangle className="w-4 h-4" /> Terms were not ticked — applicant must re-submit.
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
        <button type="button" onClick={downloadPdf} className="hv-btn hv-btn-ghost text-xs" data-testid={`app-pdf-${app.id}`}>
          <Download className="w-3.5 h-3.5" /> Download PDF
        </button>
        {editable && (
          <>
            <button
              type="button"
              onClick={() => setConfirmReject(true)}
              className="hv-btn hv-btn-ghost text-rose-300 text-xs"
              data-testid={`app-reject-${app.id}`}
            >
              <X className="w-3.5 h-3.5" /> Reject
            </button>
            <button
              type="button"
              onClick={() => setApproveOpen(true)}
              className="hv-btn hv-btn-primary text-xs"
              data-testid={`app-approve-${app.id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve
            </button>
          </>
        )}
        {app.approved_customer_id && (
          <Link to={`/customers/${app.approved_customer_id}`} className="hv-btn hv-btn-ghost text-xs">
            <ExternalLink className="w-3.5 h-3.5" /> View customer
          </Link>
        )}
      </div>

      {!editable && (
        <div className="pt-2 border-t border-white/5 text-[11px] text-[#647087]">
          Decision: <b className="uppercase" style={{ color: STATUS_CHIPS[app.status]?.color }}>{app.status}</b>
          {app.decided_by_name ? ` by ${app.decided_by_name}` : ""}
          {app.decided_at ? ` · ${new Date(app.decided_at).toLocaleString('en-GB')}` : ""}
          {app.decision_notes && <div className="mt-1 italic">"{app.decision_notes}"</div>}
        </div>
      )}

      {/* Approve modal */}
      <ConfirmModal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={approve}
        title={`Approve ${app.trading_name}?`}
        confirmLabel={busy ? "Approving…" : "Approve & unlock customer"}
        busy={busy}
        variant="warning"
        testId="approve-confirm"
        message={
          <div className="space-y-3">
            <p className="text-xs text-[#a0aabf]">
              Approving will create the customer (if it doesn't exist), unblock portal access, and
              apply the credit terms below.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#647087]">Approved credit limit (R)</label>
                <input
                  type="number"
                  value={approvedLimit}
                  onChange={(e) => setApprovedLimit(e.target.value)}
                  className="hv-input mt-1 text-sm"
                  data-testid="approve-credit-input"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#647087]">Approved payment terms</label>
                <select
                  value={approvedTerms}
                  onChange={(e) => setApprovedTerms(e.target.value)}
                  className="hv-input mt-1 text-sm"
                >
                  {["cod", "7d", "14d", "30d", "60d"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Decision notes (visible to admins only)</label>
              <VoiceTextarea
                value={decisionNotes}
                onChange={setDecisionNotes}
               context="note" />
            </div>
          </div>
        }
      />

      {/* Reject modal */}
      <ConfirmModal
        open={confirmReject}
        onClose={() => setConfirmReject(false)}
        onConfirm={reject}
        title="Reject application?"
        confirmLabel="Reject"
        testId="reject-confirm"
        message={
          <div className="space-y-2">
            <p className="text-xs text-[#a0aabf]">Provide a reason for the audit log (optional).</p>
            <VoiceTextarea
              value={rejectReason}
              onChange={setRejectReason}
             context="note" />
          </div>
        }
      />
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="text-sm text-[#dde3ec]">{value || "—"}</div>
    </div>
  );
}
